import { Box, Container, Stack, Typography, Pagination } from "@mui/material";
import { Settings } from "./Settings";
import RecommendedOrders from "./RecommendedOrders";
import { useHistory } from "react-router-dom";
import { useGlobals } from "../../hooks/useGlobals";
import { serverApi } from "../../../lib/config";
import { useState, useEffect } from "react";
import OrderService from "../../services/OrderService";
import ProductService from "../../services/ProductService";
import { Order, OrderItem, OrderUpdateInput } from "../../../lib/types/order";
import { OrderStatus } from "../../../lib/enums/order.enum";
import { Messages } from "../../../lib/config";
import { T } from "../../../lib/types/common";
import { sweetErrorHandling, sweetTopSmallSuccessAlert } from "../../../lib/sweetAlert";
import { Product } from "../../../lib/types/product";
import { ProductFormat, ProductCollection } from "../../../lib/enums/product.enum";
import { CartItem } from "../../../lib/types/search";
import "../../../css/userPage.css";
import "../../../css/order.css";
import "../../../css/products.css";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SettingsIcon from "@mui/icons-material/Settings";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import LockIcon from "@mui/icons-material/Lock";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckIcon from "@mui/icons-material/Check";
import HomeIcon from "@mui/icons-material/Home";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HistoryIcon from "@mui/icons-material/History";
import LogoutIcon from "@mui/icons-material/Logout";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ReplayIcon from "@mui/icons-material/Replay";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import FavoriteIcon from "@mui/icons-material/Favorite";
import Button from "@mui/material/Button";

type UserTab = "overview" | "order-history" | "wishlist";
type OrderHistoryFilter = "process" | "received";

const ORDER_HISTORY_FETCH_LIMIT = 100;
const ORDERS_PER_PAGE = 5;

function formatMemberSince(date: Date | string | undefined): string {
  if (!date) return "";
  const d = new Date(date);
  return d.getFullYear().toString();
}

function getFirstProduct(order: Order): Product | undefined {
  if (!order?.orderItems?.length || !order?.productData?.length) return undefined;
  const firstItem: OrderItem = order.orderItems[0];
  return order.productData.find((p: Product) => p._id === firstItem.productId);
}

function getProductForItem(order: Order, item: OrderItem): Product | undefined {
  return order.productData?.find((p: Product) => p._id === item.productId);
}

function formatOrderDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function getArrivingDate(order: Order): string {
  const d = typeof order.updatedAt === "string" ? new Date(order.updatedAt) : order.updatedAt;
  const arriving = new Date(d);
  arriving.setDate(arriving.getDate() + 5);
  return formatOrderDate(arriving);
}

function formatDeliveredDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatProductFormat(format: ProductFormat | undefined): string {
  if (!format) return "";
  const map: Record<string, string> = {
    [ProductFormat.PAPERBACK]: "Paperback",
    [ProductFormat.HARDCOVER]: "Hardcover",
    [ProductFormat.E_BOOK]: "E-Book",
    [ProductFormat.AUDIOBOOK]: "Audiobook",
  };
  return map[format] || format;
}

const COLLECTION_LABELS: Record<string, string> = {
  [ProductCollection.FICTION]: "Fiction",
  [ProductCollection.NON_FICTION]: "Non-fiction",
  [ProductCollection.ACADEMIC]: "Academic",
  [ProductCollection.KIDS]: "Kids",
  [ProductCollection.OTHER]: "Other",
};

function getCollectionLabel(collection: ProductCollection | undefined): string {
  return collection ? (COLLECTION_LABELS[collection] ?? collection) : "Other";
}

const RECENT_FAVORITES_COUNT = 3;
const FAVORITES_PAGE_LIMIT = 6;

interface UserPageProps {
  onAdd?: (item: CartItem) => void;
  onLogout?: () => void | Promise<void>;
}

export default function UserPage(props: UserPageProps) {
  const { onAdd, onLogout } = props;
  const history = useHistory();
  const { authMember } = useGlobals();
  const [activeTab, setActiveTab] = useState<UserTab>("overview");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [processOrdersList, setProcessOrdersList] = useState<Order[]>([]);
  const [finishedOrdersList, setFinishedOrdersList] = useState<Order[]>([]);
  const [processOrdersPage, setProcessOrdersPage] = useState(1);
  const [finishedOrdersPage, setFinishedOrdersPage] = useState(1);
  const [orderHistoryFilter, setOrderHistoryFilter] = useState<OrderHistoryFilter>("process");
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [ordersCount, setOrdersCount] = useState(0);

  const [recentFavorites, setRecentFavorites] = useState<Product[]>([]);
  const [favorites, setFavorites] = useState<Product[]>([]);
  const [favoritesTotal, setFavoritesTotal] = useState(0);
  const [favoritesPage, setFavoritesPage] = useState(1);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  useEffect(() => {
    if (!authMember) return;
    const orderService = new OrderService();
    orderService
      .getMyOrders({ page: 1, limit: 100, orderStatus: OrderStatus.FINISH })
      .then((data) => setOrdersCount((data || []).length))
      .catch(() => {});
  }, [authMember]);

  useEffect(() => {
    if (!authMember || activeTab !== "order-history") return;
    const orderService = new OrderService();
    orderService
      .getMyOrders({ page: 1, limit: ORDER_HISTORY_FETCH_LIMIT, orderStatus: OrderStatus.PROCESS })
      .then((data) => setProcessOrdersList(data || []))
      .catch(() => setProcessOrdersList([]));
    orderService
      .getMyOrders({ page: 1, limit: ORDER_HISTORY_FETCH_LIMIT, orderStatus: OrderStatus.FINISH })
      .then((data) => setFinishedOrdersList(data || []))
      .catch(() => setFinishedOrdersList([]));
  }, [authMember, activeTab]);

  useEffect(() => {
    if (!authMember || activeTab !== "overview") return;
    setFavoritesLoading(true);
    const productService = new ProductService();
    productService
      .getFavorites({ page: 1, limit: RECENT_FAVORITES_COUNT })
      .then((res) => {
        setRecentFavorites(res.list ?? []);
        const total = res.metaCounter?.[0]?.total;
        setFavoritesTotal(typeof total === "number" ? total : 0);
      })
      .catch(() => {
        setRecentFavorites([]);
        setFavoritesTotal(0);
      })
      .finally(() => setFavoritesLoading(false));
  }, [authMember, activeTab]);

  useEffect(() => {
    if (!authMember || activeTab !== "wishlist") return;
    setFavoritesLoading(true);
    const productService = new ProductService();
    productService
      .getFavorites({ page: favoritesPage, limit: FAVORITES_PAGE_LIMIT })
      .then((res) => {
        setFavorites(res.list ?? []);
        const total = res.metaCounter?.[0]?.total;
        setFavoritesTotal(typeof total === "number" ? total : 0);
      })
      .catch(() => {
        setFavorites([]);
        setFavoritesTotal(0);
      })
      .finally(() => setFavoritesLoading(false));
  }, [authMember, activeTab, favoritesPage]);

  const refetchFavorites = () => {
    if (!authMember) return;
    const productService = new ProductService();
    if (activeTab === "overview") {
      productService
        .getFavorites({ page: 1, limit: RECENT_FAVORITES_COUNT })
        .then((res) => {
          setRecentFavorites(res.list ?? []);
          const total = res.metaCounter?.[0]?.total;
          setFavoritesTotal(typeof total === "number" ? total : 0);
        })
        .catch(() => {});
    } else {
      productService
        .getFavorites({ page: favoritesPage, limit: FAVORITES_PAGE_LIMIT })
        .then((res) => {
          const list = res.list ?? [];
          setFavorites(list);
          const total = res.metaCounter?.[0]?.total;
          setFavoritesTotal(typeof total === "number" ? total : 0);
          if (list.length === 0 && favoritesPage > 1) setFavoritesPage(1);
        })
        .catch(() => {});
    }
  };

  const favoritesTotalPages = Math.max(1, Math.ceil(favoritesTotal / FAVORITES_PAGE_LIMIT));

  const handleFavoriteLike = async (e: React.MouseEvent, productId: string) => {
    e.stopPropagation();
    if (!authMember) {
      await sweetErrorHandling({ message: "Please log in to like products." });
      return;
    }
    try {
      const productService = new ProductService();
      await productService.likeProduct(productId);
      await sweetTopSmallSuccessAlert("Updated", 800);
      refetchFavorites();
    } catch (err) {
      console.log(err);
    }
  };

  const chooseProductHandler = (id: string) => history.push(`/products/${id}`);

  if (!authMember) {
    history.push("/");
    return null;
  }

  const navItems = [
    { id: "overview" as const, label: "Overview", Icon: PersonOutlineIcon },
    { id: "order-history" as const, label: "Order History", Icon: HistoryIcon },
    { id: "wishlist", label: "Favorites", Icon: FavoriteBorderIcon, badge: favoritesTotal },
    { id: "settings", label: "Settings", Icon: SettingsIcon },
  ];

  const openSettings = () => setSettingsOpen(true);

  const toggleViewDetails = (orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const refetchOrderHistory = () => {
    if (!authMember) return;
    const orderService = new OrderService();
    orderService
      .getMyOrders({ page: 1, limit: ORDER_HISTORY_FETCH_LIMIT, orderStatus: OrderStatus.PROCESS })
      .then((data) => setProcessOrdersList(data || []))
      .catch(() => setProcessOrdersList([]));
    orderService
      .getMyOrders({ page: 1, limit: ORDER_HISTORY_FETCH_LIMIT, orderStatus: OrderStatus.FINISH })
      .then((data) => setFinishedOrdersList(data || []))
      .catch(() => setFinishedOrdersList([]));
  };

  const finishOrderHandler = async (e: T) => {
    try {
      if (!authMember) throw new Error(Messages.error2);
      const orderId = (e.target as HTMLButtonElement).value;
      const input: OrderUpdateInput = {
        orderId,
        orderStatus: OrderStatus.FINISH,
      };
      const confirmation = window.confirm("Have you received your order?");
      if (confirmation) {
        const orderService = new OrderService();
        await orderService.updateOrder(input);
        refetchOrderHistory();
      }
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  const handleTrackPackage = (orderId: string) => {
    window.alert(`Track order ${orderId.slice(-6)} (tracking not integrated).`);
  };

  const handleBuyAgain = () => history.push("/products");
  const handleWriteReview = () => {};
  const handleInvoice = () => {};
  const closeSettings = () => setSettingsOpen(false);

  return (
    <div className="user-page">
      <Container className="user-page-container" maxWidth={false}>
        <Stack className="user-page-frame" direction="row">
          
          {/* Left sidebar */}
          <aside className="user-sidebar">
            <Box className="user-profile-card">
              <div className="user-avatar-wrap">
                <img
                  src={
                    authMember?.memberImage
                      ? `${serverApi}/${authMember.memberImage}`
                      : "/icons/default-user.svg"
                  }
                  alt=""
                  className="user-avatar"
                />
              </div>
              <h2 className="user-name">{authMember?.memberNick}</h2>
              <p className="user-email">
                {authMember?.memberPhone || authMember?.memberAdress || "No contact"}
              </p>
              <div className="user-member-since">
                <CalendarTodayIcon className="user-calendar-icon" />
                <span>Member since {formatMemberSince(authMember?.createdAt) || "—"}</span>
              </div>
              <button type="button" className="user-edit-profile-btn" onClick={openSettings}>
                Edit Profile
              </button>
            </Box>
            <nav className="user-nav">
              <ul className="user-nav-list">
                {navItems.map(({ id, label, Icon, badge }) => (
                  <li key={id}>
                    {id === "settings" ? (
                      <button
                        type="button"
                        className={`user-nav-item ${activeTab === "overview" ? "" : ""}`}
                        onClick={openSettings}
                      >
                        <Icon className="user-nav-icon" />
                        <span>{label}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`user-nav-item ${activeTab === id ? "user-nav-item--active" : ""}`}
                        onClick={() => setActiveTab(id as UserTab)}
                      >
                        <Icon className="user-nav-icon" />
                        <span>{label}</span>
                        {typeof badge === "number" && badge > 0 && (
                          <span className="user-nav-badge">{badge}</span>
                        )}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <div className="user-nav-divider" />
              <button
                type="button"
                className="user-nav-item user-nav-item--logout"
                onClick={() => onLogout?.()}
              >
                <LogoutIcon className="user-nav-icon" />
                <span>Log out</span>
              </button>
            </nav>
          </aside>

          {/* Main content */}
          <main className="user-main">
            {activeTab === "overview" && (
              <>
                <section className="user-stats">
                  <Box className="user-stat-card">
                    <div className="user-stat-header">
                      <span className="user-stat-title">Loyalty Points</span>
                      <CardMembershipIcon className="user-stat-icon" />
                    </div>
                    <p className="user-stat-value">{authMember?.memberPoints ?? 0}</p>
                    <p className="user-stat-meta">Available to redeem</p>
                  </Box>
                  <Box className="user-stat-card">
                    <div className="user-stat-header">
                      <span className="user-stat-title">Orders</span>
                      <AutoStoriesIcon className="user-stat-icon" />
                    </div>
                    <p className="user-stat-value">{ordersCount}</p>
                    <p className="user-stat-meta">Total completed</p>
                  </Box>
                  <Box className="user-stat-card">
                    <div className="user-stat-header">
                      <span className="user-stat-title">Favorites</span>
                      <BookmarkBorderIcon className="user-stat-icon" />
                    </div>
                    <p className="user-stat-value">{favoritesTotal}</p>
                    <a
                      href="#favorites"
                      className="user-stat-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab("wishlist");
                      }}
                    >
                      View favourites →
                    </a>
                  </Box>
                </section>

                {/* Recent favourites – only 3 on overview */}
                <section className="user-section user-favourites-section">
                  <h3 className="user-section-title">Recent favourites</h3>
                  {favoritesLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading…
                    </Typography>
                  ) : recentFavorites.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No favourites yet. Like books on the products page to see them here.
                    </Typography>
                  ) : (
                    <div className="products-page products-browse">
                      <div className="products-grid user-favourites-grid">
                        {recentFavorites.map((product) => {
                          const imageSrc = product.productImages?.[0]
                            ? `${serverApi}/${product.productImages[0]}`
                            : "/icons/noimage-list.svg";
                          const collectionSlug = (product.productCollection ?? "other")
                            .toLowerCase()
                            .replace("_", "-");
                          const collectionLabel = getCollectionLabel(product.productCollection);
                          return (
                            <article
                              key={product._id}
                              className={`product-card product-card-book product-card--${collectionSlug} user-favourite-card`}
                              onClick={() => chooseProductHandler(product._id)}
                            >
                              <div className="user-favourite-card-image-wrap">
                                <img
                                  src={imageSrc}
                                  alt={product.productName}
                                  className="user-favourite-card-image"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = "/icons/noimage-list.svg";
                                  }}
                                />
                                <div className={`user-favourite-card-badges user-favourite-card-badges--${collectionSlug}`}>
                                  <span className="user-favourite-card-badge">
                                    {collectionLabel.toUpperCase()}
                                  </span>
                                </div>
                                {onAdd && (
                                  <button
                                    type="button"
                                    className="user-favourite-card-add-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onAdd({
                                        _id: product._id,
                                        quantity: 1,
                                        name: product.productName,
                                        price: product.productPrice,
                                        image: product.productImages?.[0] ?? "",
                                      });
                                    }}
                                  >
                                    <AddShoppingCartIcon sx={{ fontSize: 18 }} />
                                    Add to basket
                                  </button>
                                )}
                              </div>
                              <Typography variant="subtitle1" className="product-card-title">
                                {product.productName}
                              </Typography>
                              {product.productAuthor && (
                                <Typography variant="body2" color="text.secondary" className="product-card-author">
                                  {product.productAuthor}
                                </Typography>
                              )}
                              <div className="product-card-divider" />
                              <div className="product-card-footer">
                                <div className="product-card-footer-left">
                                  <Typography variant="body1" className="product-card-price">
                                    ${product.productPrice.toFixed(2)}
                                  </Typography>
                                </div>
                                <div className="product-card-footer-divider" />
                                <div className="product-card-footer-stats">
                                  <span className="product-card-views">
                                    <RemoveRedEyeIcon sx={{ fontSize: 22 }} />
                                    <span className="product-card-stat-num">{product.productViews ?? 0}</span>
                                  </span>
                                  <button
                                    type="button"
                                    className="product-card-likes product-card-liked"
                                    onClick={(e) => handleFavoriteLike(e, product._id)}
                                    aria-label="Unlike"
                                  >
                                    <FavoriteIcon sx={{ fontSize: 22, color: "#e53935" }} />
                                    <span className="product-card-stat-num">{product.productLikes ?? 0}</span>
                                  </button>
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>

                {/* Account settings links */}
                <section className="user-section">
                  <h3 className="user-section-title">Account Settings</h3>
                  <ul className="user-settings-list">
                    <li>
                      <button
                        type="button"
                        className="user-settings-row"
                        onClick={openSettings}
                      >
                        <LockIcon className="user-settings-icon" />
                        <div className="user-settings-text">
                          <span className="user-settings-label">Login & Security</span>
                          <span className="user-settings-desc">
                            Update password and profile details
                          </span>
                        </div>
                        <ChevronRightIcon className="user-settings-arrow" />
                      </button>
                    </li>
                    <li>
                      <button type="button" className="user-settings-row" disabled>
                        <NotificationsIcon className="user-settings-icon" />
                        <div className="user-settings-text">
                          <span className="user-settings-label">Notifications</span>
                          <span className="user-settings-desc">
                            Manage email and push notifications
                          </span>
                        </div>
                        <ChevronRightIcon className="user-settings-arrow" />
                      </button>
                    </li>
                    <li>
                      <button type="button" className="user-settings-row" disabled>
                        <CreditCardIcon className="user-settings-icon" />
                        <div className="user-settings-text">
                          <span className="user-settings-label">Payment Methods</span>
                          <span className="user-settings-desc">
                            Manage your saved payment methods
                          </span>
                        </div>
                        <ChevronRightIcon className="user-settings-arrow" />
                      </button>
                    </li>
                  </ul>
                </section>
              </>
            )}

            {activeTab === "order-history" && (
              <section className="user-section user-order-history">
                <h3 className="user-section-title">Order History</h3>
                <div className="user-order-history-tabs">
                  <button
                    type="button"
                    className={`user-order-history-tab ${orderHistoryFilter === "process" ? "user-order-history-tab--active" : ""}`}
                    onClick={() => setOrderHistoryFilter("process")}
                  >
                    Process
                  </button>
                  <button
                    type="button"
                    className={`user-order-history-tab ${orderHistoryFilter === "received" ? "user-order-history-tab--active" : ""}`}
                    onClick={() => setOrderHistoryFilter("received")}
                  >
                    Received
                  </button>
                </div>
                <div className="order-page user-page-order-context">
                  {orderHistoryFilter === "process" && (
                    <Stack className="process-orders-stack">
                      {processOrdersList.length === 0 ? (
                        <Box className="user-order-empty">
                          <img src="/icons/noimage-list.svg" alt="" style={{ width: 120, height: 120, opacity: 0.5 }} />
                          <span>No orders in process.</span>
                        </Box>
                      ) : (
                        processOrdersList
                          .slice((processOrdersPage - 1) * ORDERS_PER_PAGE, processOrdersPage * ORDERS_PER_PAGE)
                          .map((order: Order) => {
                          const firstProduct = getFirstProduct(order);
                          const orderIdShort = `BK-${order._id.slice(-4)}`;
                          const hasOtherProducts = (order.orderItems?.length ?? 0) > 1;
                          const isExpanded = expandedOrderId === order._id;
                          if (!firstProduct) return null;
                          const imagePath = firstProduct.productImages?.length
                            ? `${serverApi}/${firstProduct.productImages[0]}`
                            : "/icons/noimage-list.svg";
                          return (
                            <Box key={order._id} className="process-order-card">
                              <Box className="process-order-tracking-header">
                                <Box className="process-order-tracking-header-left">
                                  <span className="process-order-tracking-icon-wrap">
                                    <LocalShippingIcon className="process-order-truck-icon" />
                                  </span>
                                  <Box>
                                    <span className="process-order-status-label">IN TRANSIT</span>
                                    <p className="process-order-arriving">
                                      Arriving by {getArrivingDate(order)}
                                    </p>
                                  </Box>
                                </Box>
                                <Box className="process-order-tracking-header-right">
                                  <span className="process-order-id">Order #{orderIdShort}</span>
                                  <span className="process-order-divider-v">|</span>
                                  <button
                                    type="button"
                                    className="process-order-track-link"
                                    onClick={() => handleTrackPackage(order._id)}
                                  >
                                    Track Package
                                  </button>
                                </Box>
                              </Box>
                              <Box className="paused-order-card-body">
                                <img
                                  src={imagePath}
                                  alt={firstProduct.productName}
                                  className="paused-order-product-img"
                                />
                                <Box className="paused-order-content">
                                  <h3 className="paused-order-product-title">
                                    {firstProduct.productName}
                                  </h3>
                                  <p className="paused-order-product-author">
                                    {firstProduct.productAuthor || "No author"}
                                  </p>
                                  <Box className="process-order-steps process-order-steps--below-author process-order-steps--small">
                                    <Box className="process-order-step process-order-step-done">
                                      <span className="process-order-step-icon"><CheckIcon /></span>
                                      <span className="process-order-step-label">Placed</span>
                                    </Box>
                                    <Box className="process-order-step-bar process-order-step-bar-done" />
                                    <Box className="process-order-step process-order-step-done">
                                      <span className="process-order-step-icon"><CheckIcon /></span>
                                      <span className="process-order-step-label">Paid</span>
                                    </Box>
                                    <Box className="process-order-step-bar process-order-step-bar-done" />
                                    <Box className="process-order-step process-order-step-current">
                                      <span className="process-order-step-icon"><LocalShippingIcon /></span>
                                      <span className="process-order-step-label">On Way</span>
                                    </Box>
                                    <Box className="process-order-step-bar" />
                                    <Box className="process-order-step process-order-step-pending">
                                      <span className="process-order-step-icon"><HomeIcon /></span>
                                      <span className="process-order-step-label">Delivered</span>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                              {isExpanded && hasOtherProducts && (
                                <Box className="paused-order-details-list">
                                  {order.orderItems.map((item: OrderItem) => {
                                    const product = getProductForItem(order, item);
                                    if (!product) return null;
                                    const itemImg = product.productImages?.length
                                      ? `${serverApi}/${product.productImages[0]}`
                                      : "/icons/noimage-list.svg";
                                    return (
                                      <Box key={item._id} className="paused-order-detail-row">
                                        <img src={itemImg} alt={product.productName} className="paused-order-detail-img" />
                                        <Box className="paused-order-detail-info">
                                          <span className="paused-order-detail-name">{product.productName}</span>
                                          {product.productAuthor && (
                                            <p className="paused-order-detail-author">{product.productAuthor}</p>
                                          )}
                                          <span className="paused-order-detail-qty">
                                            {item.itemQuantity} × ${item.itemPrice.toFixed(2)} = $
                                            {(item.itemQuantity * item.itemPrice).toFixed(2)}
                                          </span>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                              <Box className="process-order-footer">
                                <Box className="process-order-last-scan">
                                  <LocationOnIcon className="process-order-last-scan-icon" />
                                  <span>Last scan: In transit</span>
                                </Box>
                                <Box className="process-order-footer-actions">
                                  {hasOtherProducts && (
                                    <Button
                                      variant="outlined"
                                      className="process-order-view-details"
                                      onClick={() => toggleViewDetails(order._id)}
                                    >
                                      {isExpanded ? "Hide details" : "View details"}
                                    </Button>
                                  )}
                                  <Button
                                    variant="outlined"
                                    className="process-order-received-btn"
                                    value={order._id}
                                    onClick={finishOrderHandler}
                                  >
                                    Mark as Received
                                  </Button>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })
                      )}
                      {processOrdersList.length > ORDERS_PER_PAGE && (
                        <Box className="user-order-pagination" sx={{ mt: 2, justifyContent: "center" }}>
                          <Pagination
                            count={Math.ceil(processOrdersList.length / ORDERS_PER_PAGE)}
                            page={processOrdersPage}
                            onChange={(_, p) => setProcessOrdersPage(p)}
                            color="primary"
                            size="medium"
                          />
                        </Box>
                      )}
                    </Stack>
                  )}
                  {orderHistoryFilter === "received" && (
                    <Stack className="finished-orders-stack">
                      {finishedOrdersList.length === 0 ? (
                        <Box className="user-order-empty">
                          <img src="/icons/noimage-list.svg" alt="" style={{ width: 120, height: 120, opacity: 0.5 }} />
                          <span>No delivered orders yet.</span>
                        </Box>
                      ) : (
                        finishedOrdersList
                          .slice((finishedOrdersPage - 1) * ORDERS_PER_PAGE, finishedOrdersPage * ORDERS_PER_PAGE)
                          .map((order: Order) => {
                          const firstProduct = getFirstProduct(order);
                          const orderIdShort = order._id.slice(-4);
                          const hasOtherProducts = (order.orderItems?.length ?? 0) > 1;
                          const isExpanded = expandedOrderId === order._id;
                          const deliveredDate = formatDeliveredDate(
                            order.updatedAt ?? order.createdAt
                          );
                          if (!firstProduct) return null;
                          const imagePath = firstProduct.productImages?.length
                            ? `${serverApi}/${firstProduct.productImages[0]}`
                            : "/icons/noimage-list.svg";
                          const formatLabel = formatProductFormat(firstProduct.productFormat);
                          const subtitle = [formatLabel, firstProduct.productAuthor]
                            .filter(Boolean)
                            .join(" • ");
                          return (
                            <Box key={order._id} className="finished-order-card">
                              <Box className="finished-order-tracking-header">
                                <Box className="finished-order-tracking-header-left">
                                  <Box className="finished-order-tracking-icon-wrap">
                                    <CheckCircleIcon className="finished-order-truck-icon" />
                                  </Box>
                                  <Box>
                                    <span className="finished-order-status-label">DELIVERED</span>
                                    <span className="finished-order-arriving">{deliveredDate}</span>
                                  </Box>
                                </Box>
                                <Box className="finished-order-tracking-header-right">
                                  <span className="finished-order-id">Order #BK-{orderIdShort}</span>
                                  <span className="finished-order-divider-v">|</span>
                                  <button
                                    type="button"
                                    className="finished-order-invoice-link"
                                    onClick={handleInvoice}
                                  >
                                    Invoice
                                  </button>
                                </Box>
                              </Box>
                              <Box className="finished-order-body">
                                <img
                                  src={imagePath}
                                  alt={firstProduct.productName}
                                  className="finished-order-product-img"
                                />
                                <Box className="finished-order-content">
                                  <h3 className="finished-order-product-title">
                                    {firstProduct.productName}
                                  </h3>
                                  {subtitle && (
                                    <p className="finished-order-product-meta">{subtitle}</p>
                                  )}
                                  <Box className="finished-order-steps process-order-steps process-order-steps--below-author process-order-steps--small">
                                    <Box className="process-order-step process-order-step-done">
                                      <span className="process-order-step-icon"><CheckIcon /></span>
                                      <span className="process-order-step-label">Placed</span>
                                    </Box>
                                    <Box className="process-order-step-bar process-order-step-bar-done" />
                                    <Box className="process-order-step process-order-step-done">
                                      <span className="process-order-step-icon"><CheckIcon /></span>
                                      <span className="process-order-step-label">Paid</span>
                                    </Box>
                                    <Box className="process-order-step-bar process-order-step-bar-done" />
                                    <Box className="process-order-step process-order-step-done">
                                      <span className="process-order-step-icon"><LocalShippingIcon /></span>
                                      <span className="process-order-step-label">On Way</span>
                                    </Box>
                                    <Box className="process-order-step-bar process-order-step-bar-done" />
                                    <Box className="process-order-step process-order-step-done">
                                      <span className="process-order-step-icon"><HomeIcon /></span>
                                      <span className="process-order-step-label">Delivered</span>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                              {isExpanded && hasOtherProducts && (
                                <Box className="finished-order-details-list">
                                  {order.orderItems.map((item: OrderItem) => {
                                    const product = getProductForItem(order, item);
                                    if (!product) return null;
                                    const itemImg = product.productImages?.length
                                      ? `${serverApi}/${product.productImages[0]}`
                                      : "/icons/noimage-list.svg";
                                    return (
                                      <Box key={item._id} className="finished-order-detail-row">
                                        <img src={itemImg} alt={product.productName} className="finished-order-detail-img" />
                                        <Box className="finished-order-detail-info">
                                          <span className="finished-order-detail-name">{product.productName}</span>
                                          {product.productAuthor && (
                                            <p className="finished-order-detail-author">{product.productAuthor}</p>
                                          )}
                                          <span className="finished-order-detail-qty">
                                            {item.itemQuantity} × ${item.itemPrice.toFixed(2)} = $
                                            {(item.itemQuantity * item.itemPrice).toFixed(2)}
                                          </span>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                              <Box className="finished-order-footer">
                                <Box className="finished-order-footer-left">
                                  <Button
                                    variant="outlined"
                                    className="finished-order-buy-again-btn"
                                    onClick={handleBuyAgain}
                                    startIcon={<ReplayIcon />}
                                  >
                                    Buy Again
                                  </Button>
                                  <button
                                    type="button"
                                    className="finished-order-write-review-link"
                                    onClick={handleWriteReview}
                                  >
                                    Write a Review
                                  </button>
                                </Box>
                                {hasOtherProducts && (
                                  <Button
                                    variant="outlined"
                                    className="finished-order-view-details"
                                    onClick={() => toggleViewDetails(order._id)}
                                  >
                                    {isExpanded ? "Hide details" : "View details"}
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          );
                        })
                      )}
                      {finishedOrdersList.length > ORDERS_PER_PAGE && (
                        <Box className="user-order-pagination" sx={{ mt: 2, justifyContent: "center" }}>
                          <Pagination
                            count={Math.ceil(finishedOrdersList.length / ORDERS_PER_PAGE)}
                            page={finishedOrdersPage}
                            onChange={(_, p) => setFinishedOrdersPage(p)}
                            color="primary"
                            size="medium"
                          />
                        </Box>
                      )}
                    </Stack>
                  )}
                  <RecommendedOrders
                    finishedOrders={finishedOrdersList}
                    onAdd={onAdd}
                    onProductClick={chooseProductHandler}
                    compact={orderHistoryFilter === "process" && processOrdersList.length === 0}
                  />
                </div>
              </section>
            )}

            {activeTab === "wishlist" && (
              <section className="user-section user-favourites-section">
                <h3 className="user-section-title">Favourites</h3>
                {favoritesLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                ) : favorites.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No favourites yet. Like books on the products page to see them here.
                  </Typography>
                ) : (
                  <>
                    <div className="products-page products-browse">
                      <div className="products-grid user-favourites-grid">
                        {favorites.map((product) => {
                          const imageSrc = product.productImages?.[0]
                            ? `${serverApi}/${product.productImages[0]}`
                            : "/icons/noimage-list.svg";
                          const collectionSlug = (product.productCollection ?? "other")
                            .toLowerCase()
                            .replace("_", "-");
                          const collectionLabel = getCollectionLabel(product.productCollection);
                          return (
                            <article
                              key={product._id}
                              className={`product-card product-card-book product-card--${collectionSlug} user-favourite-card`}
                              onClick={() => chooseProductHandler(product._id)}
                            >
                              <div className="user-favourite-card-image-wrap">
                                <img
                                  src={imageSrc}
                                  alt={product.productName}
                                  className="user-favourite-card-image"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = "/icons/noimage-list.svg";
                                  }}
                                />
                                <div className={`user-favourite-card-badges user-favourite-card-badges--${collectionSlug}`}>
                                  <span className="user-favourite-card-badge">
                                    {collectionLabel.toUpperCase()}
                                  </span>
                                </div>
                                {onAdd && (
                                  <button
                                    type="button"
                                    className="user-favourite-card-add-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onAdd({
                                        _id: product._id,
                                        quantity: 1,
                                        name: product.productName,
                                        price: product.productPrice,
                                        image: product.productImages?.[0] ?? "",
                                      });
                                    }}
                                  >
                                    <AddShoppingCartIcon sx={{ fontSize: 18 }} />
                                    Add to basket
                                  </button>
                                )}
                              </div>
                              <Typography variant="subtitle1" className="product-card-title">
                                {product.productName}
                              </Typography>
                              {product.productAuthor && (
                                <Typography variant="body2" color="text.secondary" className="product-card-author">
                                  {product.productAuthor}
                                </Typography>
                              )}
                              <div className="product-card-divider" />
                              <div className="product-card-footer">
                                <div className="product-card-footer-left">
                                  <Typography variant="body1" className="product-card-price">
                                    ${product.productPrice.toFixed(2)}
                                  </Typography>
                                </div>
                                <div className="product-card-footer-divider" />
                                <div className="product-card-footer-stats">
                                  <span className="product-card-views">
                                    <RemoveRedEyeIcon sx={{ fontSize: 22 }} />
                                    <span className="product-card-stat-num">{product.productViews ?? 0}</span>
                                  </span>
                                  <button
                                    type="button"
                                    className="product-card-likes product-card-liked"
                                    onClick={(e) => handleFavoriteLike(e, product._id)}
                                    aria-label="Unlike"
                                  >
                                    <FavoriteIcon sx={{ fontSize: 22, color: "#e53935" }} />
                                    <span className="product-card-stat-num">{product.productLikes ?? 0}</span>
                                  </button>
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                    {favoritesTotalPages > 1 && (
                      <Box className="user-favourites-pagination" sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                        <Pagination
                          count={favoritesTotalPages}
                          page={favoritesPage}
                          onChange={(_, page) => setFavoritesPage(page)}
                          color="primary"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    )}
                  </>
                )}
              </section>
            )}
          </main>
        </Stack>
      </Container>

      {/* Settings modal */}
      {settingsOpen && (
        <div className="user-settings-modal-backdrop" onClick={closeSettings} aria-hidden>
          <div
            className="user-settings-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-modal-title"
          >
            <div className="user-settings-modal-header">
              <h2 id="settings-modal-title" className="user-settings-modal-title">
                Edit Profile
              </h2>
              <button
                type="button"
                className="user-settings-modal-close"
                onClick={closeSettings}
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="user-settings-modal-body">
              <Settings onClose={closeSettings} onSaveSuccess={closeSettings} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
